import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Col, Container, Row } from 'react-bootstrap';
import { CardBody } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { Btn } from '../../AbstractElements';
import {
  useGetManualOrdersQuery,
  useUpdateManualOrderStatusMutation,
  useSendManualOrderEmailUpdatesMutation,
} from '../../features/manualOrdersApiSlice';
import {
  downloadCsv,
  downloadMultipleOrderPdfs,
  extractTrackingId,
  generateExternalOrderPdfBuffer,
  generateExternalLabelPdfBuffer,
  resolvePreparationDate,
} from '../../utils';
import { useReactToPrint } from 'react-to-print';
import ConfirmationModal from '../UiKits/ConfirmationModal';
import { producedDays, monthsFromOrderMonths, isOmega3OnlyOrder } from '../../utils/labelDays';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import {
  useGetIngredientPricesQuery,
  useGetIngredientsCodesQuery,
} from '../../features/ordersApiSlice';

const ManualOrders = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [status, setStatus] = useState('pending');
  const [details, setDetails] = useState('pending');
  const [externalOrder, setExternalOrder] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [showError, setShowError] = useState({ status: '', details: '' });
  const [selectedRows, setSelectedRows] = useState();
  console.log('selectedRows => ', selectedRows);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [isDownloadingSummary, setIsDownloadingSummary] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isDownloadingLabel, setIsDownloadingLabel] = useState(false);
  console.log('externalOrder => ', externalOrder);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const shippingAddressRef = useRef();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: externalOrders,
    isLoading,
    isSuccess,
    refetch,
  } = useGetManualOrdersQuery({
    page: currentPage,
    limit: pageSize,
    search: debouncedSearchTerm,
    status: statusFilter,
  });
  const { data: ingredientCodes } = useGetIngredientsCodesQuery();
  const { data: ingredientPrices } = useGetIngredientPricesQuery('en');
  const [updateOrderStatus] = useUpdateManualOrderStatusMutation();
  const [sendEmailUpdates, { isLoading: sendEmailUpdatesLoading }] =
    useSendManualOrderEmailUpdatesMutation();

  console.log('DATA CUSTOMERS', externalOrders);

  useEffect(() => {
    refetch();
  }, [refetch, currentPage, pageSize, debouncedSearchTerm, statusFilter]);

  useEffect(() => {
    const fn = () => {
      if (isSuccess && externalOrders?.orders) {
        setData(externalOrders?.orders || []);
        setTotalRows(externalOrders?.pagination?.total || 0);
      }
    };
    fn();
  }, [externalOrders]);

  const handleAction = async (row) => {
    setExternalOrder(row);
  };

  const printShipAddress = useReactToPrint({
    content: () => shippingAddressRef.current,
  });

  const toggleModal = () => {
    showErrorMessage('status', '');
    showErrorMessage('details', '');

    if (status === '') {
      showErrorMessage('status', 'Please select status.');
    } else {
      if (externalOrder?.id) {
        setModalOpen(!modalOpen);
      }
    }
  };

  const showErrorMessage = (object, message) => {
    setShowError((prevValues) => ({
      ...prevValues,
      [object]: message,
    }));
  };

  const handleConfirmAction = () => {
    handleUpdateStatus();
  };

  const handleUpdateStatus = async () => {
    try {
      const res = await updateOrderStatus({
        orderId: externalOrder?.id,
        status: status,
        details: details,
      }).unwrap();
      console.log(res);
      toast.success(res?.message || 'Order status updated successfully');
    } catch (e) {
      console.log(e);
    }
  };

  const cogs = useMemo(
    () =>
      externalOrder?.ingredients?.reduce((sum, item) => {
        return sum + (parseFloat(item.cogs) || 0);
      }, 0),
    [externalOrder],
  );
  const numberOfMonths = parseInt(externalOrder?.order_months) || 1;
  const totalOrderCogs = cogs * 30 * numberOfMonths;

  const tableColumns = [
    {
      name: 'Id',
      selector: (row) => `${row?.id}`,
      width: '70px',
    },
    {
      name: 'Internal Order Id',
      selector: (row) => `${row?.internal_order_id ? row.internal_order_id : ''}`,
    },
    {
      name: 'Order Id',
      selector: (row) => `${row?.order_id}`,
    },
    {
      name: 'Name',
      selector: (row) => row?.user?.full_name,
      sortable: true,
      center: false,
    },
    {
      name: 'Email',
      selector: (row) => `${row?.user?.email}`,
      sortable: true,
      center: false,
    },
    {
      name: 'Phone Number',
      selector: (row) => `${row?.user?.phone || ''}`,
      center: false,
    },
    {
      name: 'Preparation Date',
      // SHI-564 — orders created before the server started stamping this have no
      // preparation_date, and `new Date(null)` rendered every one of them as Jan 01 1970.
      selector: (row) => resolvePreparationDate(row),
      sortable: true,
      center: false,
      cell: (row) => resolvePreparationDate(row).toDateString(),
    },
    {
      name: 'Source Company Name',
      selector: (row) => `${row.user?.source_company_name}`,
      center: false,
      sortable: true,
    },
    {
      name: 'Status',
      selector: (row) => `${row?.status}`,
      center: false,
      sortable: true,
    },
    {
      name: 'Order Months',
      selector: (row) => `${row.order_months}`,
      // SHI-528 — the real pouch count under the duration, so whoever prints labels sees
      // what the bag actually holds. An overridden run is called out: months x 31 is no
      // longer the answer for every order.
      cell: (row) => {
        // An Omega-3-only order has no dosed pouches, so there is no count to show.
        if (isOmega3OnlyOrder(row)) return <div>{row.order_months}</div>;
        const pouches = producedDays(monthsFromOrderMonths(row.order_months), row.dosing_days);
        return (
          <div>
            <div>{row.order_months}</div>
            <div
              style={{
                fontSize: '11px',
                lineHeight: 1.2,
                color: row.dosing_days ? '#b45309' : '#6b7280',
                fontWeight: row.dosing_days ? 600 : 400,
              }}
            >
              {pouches} pouches{row.dosing_days ? ' · custom' : ''}
            </div>
          </div>
        );
      },
      center: false,
      sortable: true,
    },
    {
      name: 'Email Count',
      selector: (row) => row['email_count'],
      sortable: true,
      center: false,
    },
    {
      name: 'Action',
      width: '140px',
      cell: (row) => (
        <Row className="flex flex-row">
          <Btn attrBtn={{ color: 'primary', onClick: () => handleAction(row) }} className="col-6">
            {'View'}
          </Btn>
          {/* <Btn
            attrBtn={{
              color: 'primary',
              onClick: () =>
                navigate(`/admin/manual-orders-pdf`, {
                  state: {
                    order: row,
                  },
                }),
            }}
            className="col-6"
          >
            Details
          </Btn> */}
        </Row>
      ),
    },
  ];

  const handleRowSelected = useCallback((state) => {
    setSelectedRows(state.selectedRows);
  }, []);

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
  };

  const handleStatusFilter = (event) => {
    const value = event.target.value;
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPageSize(newPerPage);
    setCurrentPage(page);
  };

  const downloadAllSelectedOrders = async () => {
    if (!selectedRows?.length) {
      toast.error('Please select at least one order to download');
      return;
    }

    if (!ingredientPrices) {
      toast.error('Required data is still loading. Please wait and try again.');
      return;
    }

    const loadingToast = toast.loading('Generating PDFs...');
    try {
      setIsDownloadingPdf(true);
      // Show loading toast
      const allOrdersData = selectedRows;

      // Generate PDF for each order
      const generateOrderPdf = async (order) => {
        // Create deep copy of the order data
        const mergedOrder = JSON.parse(JSON.stringify(order));
        // SHI-564 — manual orders print the preparation date they were produced on. Only
        // this screen asks for it, so external partner sheets are unchanged.
        return await generateExternalOrderPdfBuffer(mergedOrder, ingredientPrices, ingredientCodes, {
          showPreparationDate: true,
        });
      };

      // Download all PDFs as a single file
      await downloadMultipleOrderPdfs(allOrdersData, generateOrderPdf);

      // Close loading toast and show success
      toast.dismiss(loadingToast);
      toast.success(`Successfully downloaded ${selectedRows.length} order PDF(s)`);
    } catch (error) {
      console.error('Error downloading orders:', error);
      toast.error('Error generating PDFs. Please try again.');
    } finally {
      toast.dismiss(loadingToast);
      setIsDownloadingPdf(false);
    }
  };

  const downloadAllSelectedLabels = async () => {
    if (!selectedRows?.length) {
      toast.error('Please select at least one order to download labels');
      return;
    }

    if (!ingredientPrices) {
      toast.error('Required data is still loading. Please wait and try again.');
      return;
    }

    // Prompt for batch number
    const input = window.prompt('Enter batch number (e.g. 123):');
    if (!input || !input.trim()) return;
    const batch = 'PROD' + input.trim();

    // NB: no server batch-assignment here. The external flow writes production_batch to
    // external_orders by id, but ops_manual_orders has no such column and the id spaces overlap
    // (so it would mis-tag a real external order). The batch number is only used for the filename.

    const loadingToast = toast.loading('Generating Labels...');
    try {
      setIsDownloadingLabel(true);
      // SHI-528 — an Omega-3-only order has no dosed pouches, so it has no ingredient label.
      // Printing one produced a blank page with a 0g net quantity, which then had to be
      // pulled out of the stack by hand. Skip them: 20 selected with 1 Omega-3-only = 19 pages.
      const allOrdersData = selectedRows.filter((o) => !isOmega3OnlyOrder(o));
      const skipped = selectedRows.length - allOrdersData.length;

      if (!allOrdersData.length) {
        toast.dismiss(loadingToast);
        toast.error('Nothing to print — every selected order is Omega-3 only, which has no ingredient label.');
        setIsDownloadingLabel(false);
        return;
      }

      const generateLabelPdf = async (order) => {
        const mergedOrder = JSON.parse(JSON.stringify(order));
        return await generateExternalLabelPdfBuffer(mergedOrder, ingredientPrices, ingredientCodes);
      };

      await downloadMultipleOrderPdfs(allOrdersData, generateLabelPdf, `Ingredients Labels Manual Orders - ${batch}.pdf`);

      toast.dismiss(loadingToast);
      toast.success(
        `Successfully downloaded ${allOrdersData.length} label PDF(s)`
        + (skipped ? ` — skipped ${skipped} Omega-3-only order${skipped === 1 ? '' : 's'} (no pouches)` : ''),
      );
    } catch (error) {
      console.error('Error downloading labels:', error);
      toast.error('Error generating label PDFs. Please try again.');
    } finally {
      toast.dismiss(loadingToast);
      setIsDownloadingLabel(false);
    }
  };

  const downloadWeightCalculationsCSV = async () => {
    if (!selectedRows?.length) {
      toast.error('Please select at least one order to download CSV');
      return;
    }

    try {
      setIsDownloadingCsv(true);
      const loadingToast = toast.loading('Generating CSV...');
      const allOrdersData = selectedRows;

      const allIngredients = new Set();
      allOrdersData.forEach((order) => {
        order.ingredients?.forEach((ingredient) => {
          allIngredients.add(ingredient.ingredientName);
        });
      });

      const csvData = [];
      const headers = ['Ingredient'];

      allOrdersData.forEach((order) => {
        const orderId = order?.internal_order_id;
        if (orderId) {
          headers.push(`Order ${orderId}`);
        }
      });

      Array.from(allIngredients).forEach((ingredientName) => {
        const row = { Ingredient: ingredientName };

        allOrdersData.forEach((order) => {
          const orderId = order?.internal_order_id;
          const duration = order?.order_months;
          // SHI-528 — the raw weight to dose is driven by what is actually PRODUCED, so an
          // overridden order must use its real pouch count, not order_months x 31.
          const days = producedDays(monthsFromOrderMonths(duration), order?.dosing_days);
          const ingredient = order.ingredients?.find((ing) => ing.ingredientName === ingredientName);

          if (ingredient) {
            let quantity = ingredient.quantity;
            // if (editedIngredients[orderId]?.[ingredient.id]) {
            //   quantity = editedIngredients[orderId][ingredient.id];
            // }
            const price = ingredientPrices?.data?.find(
              (item) => item?.ingredient_id === ingredient?.ingredientId,
            );
            const activePercentage = price?.active_percentage || 100;
            console.log('activePercentage => ', price?.name, quantity, activePercentage, days);
            const weight = (parseFloat(quantity) * days) / activePercentage;
            row[`Order ${orderId}`] = Math.round(weight);
          } else {
            row[`Order ${orderId}`] = '0.00';
          }
        });

        csvData.push(row);
      });
      downloadCsv(csvData, headers);
      toast.dismiss(loadingToast);
      toast.success(`Successfully downloaded weight calculations CSV`);
    } catch (error) {
      console.error('Error generating CSV:', error);
      toast.error('Error generating CSV. Please try again.');
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  const downloadSummaryOrdersCSV = async () => {
    if (!selectedRows?.length) {
      toast.error('Please select at least one order to download summary CSV');
      return;
    }

    try {
      setIsDownloadingSummary(true);
      const loadingToast = toast.loading('Generating Summary CSV...');
      const allOrdersData = selectedRows;
      const csvData = [];

      allOrdersData.forEach((order, index) => {
        const orderId = order?.internal_order_id;
        const duration = order?.order_months;
        const isFirstOrder = order?.isFirstOrder === 1 ? 'Y' : 'N';
        // SHI-528 — production figures, so the real pouch count including any override.
        const days = producedDays(monthsFromOrderMonths(duration), order?.dosing_days);
        const monthsToPrepare = order?.order_months;
        const dailyPouches = days;
        const months = duration === '1' ? '1 month' : duration + ' months';

        // Get edited name if available
        // const tableRow = selectedRows.find((row) => row.internal_order_id === orderId);
        let customerName = `${order?.user?.full_name}`;
        // if (tableRow && editedData[tableRow.id]?.name) {
        //   customerName = editedData[tableRow.id].name;
        // }

        // Calculate total weight per daily pouch
        let totalWeight = 0;
        order.ingredients?.forEach((ingredient) => {
          let quantity = ingredient.quantity;
          // if (editedIngredients[orderId]?.[ingredient.id]) {
          //   quantity = editedIngredients[orderId][ingredient.id];
          // }
          const price = ingredientPrices?.data?.find(
            (item) => item?.ingredient_id === parseInt(ingredient?.ingredientId),
          );
          const activePercentage = price?.active_percentage || 100;
          const weight = (parseFloat(quantity) * days) / activePercentage;
          // console.log('weight => ', price?.name, quantity, activePercentage, days, weight);
          totalWeight += weight;
        });

        const weightPerDailyPouch = totalWeight / days;

        csvData.push({
          '#': index + 1,
          'Order id': orderId,
          Plan: duration,
          'Welcome pack (Y/N)': isFirstOrder,
          'Weight per daily pouch': Math.round(weightPerDailyPouch * 100) / 100,
          '#months to prepare': monthsToPrepare,
          '#daily pouches': dailyPouches,
          name: customerName,
          month: months,
        });
      });

      const headers = [
        '#',
        'Order id',
        'Plan',
        'Welcome pack (Y/N)',
        'Weight per daily pouch',
        '#months to prepare',
        '#daily pouches',
        'name',
        'month',
      ];

      downloadCsv(csvData, headers);
      toast.dismiss(loadingToast);
      toast.success(`Successfully downloaded summary orders CSV`);
    } catch (error) {
      console.error('Error generating summary CSV:', error);
      toast.error('Error generating summary CSV. Please try again.');
    } finally {
      setIsDownloadingSummary(false);
    }
  };

  const createShipment = async () => {
    // SHI-528 — see OrdersPdf: never build "?orderIds=undefined" for the next page.
    const orderIds = (selectedRows || []).map((item) => item?.id).filter((id) => id != null);
    if (!orderIds.length) {
      toast.error('Please select at least one order to create labels.');
      return;
    }
    const allSameCompany = selectedRows.every(
      (item) => item?.user?.source_company_name === selectedRows[0]?.user?.source_company_name,
    );
    if (!allSameCompany) {
      toast.error('Please select labels only for the same partner!');
      return;
    }
    navigate(`/admin/manual-orders-pdf/shipment-labels?orderIds=${JSON.stringify(orderIds)}`);
  };
  console.log('selectedRows => ', selectedRows);

  const getShipmentTracking = async () => {
    if (!selectedRows?.length) {
      toast.error('Please select at least one order to download summary CSV');
      return;
    }
    const ordersWithTrackingId = selectedRows?.filter((item) => !!item?.order_tracking_link);
    if (ordersWithTrackingId?.length === 0) {
      toast.error('No order with tracking id');
      return;
    }
    console.log('ordersWithTrackingId => ', ordersWithTrackingId);
    const trackingIds = ordersWithTrackingId?.map((item) => extractTrackingId(item?.order_tracking_link));
    console.log('trackingIds => ', trackingIds);
    navigate(`/admin/manual-orders-pdf/shipment-tracking?trackingIds=${JSON.stringify(trackingIds)}`, {
      state: {
        ordersWithTrackingId,
      },
    });
  };
  console.log(
    'data => ',
    data?.filter((item) => item?.order_tracking_link),
  );
  console.log('selectedRows => ', selectedRows);

  const sendEmailUpdatesSubmit = async () => {
    try {
      if (selectedRows?.length === 0) {
        toast.error('Please select orders to send emails!');
        return;
      }
      const loadingToast = toast.loading('Sending email updates...');
      const orderIds = selectedRows?.map((item) => item?.id);
      const res = await sendEmailUpdates({ orderIds });
      toast.dismiss(loadingToast);
      if (res?.error?.data) {
        toast.error(res?.error?.data?.message || 'Something went wrong!');
      } else if (res?.data?.sent === 0) {
        toast.error(res?.data?.message || 'No emails sent — selected orders have no email address.');
      } else {
        toast.success(res?.data?.message || 'Order updates sent successfully!');
      }
    } catch (err) {
      toast.error(err?.message || `Something went wrong`);
      console.log('Error => ', err);
    }
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <>
      <h1 className="t-text-xl t-font-bold t-mb-2 t-ml-2 md:t-ml-3 t-text-secondary">Manual Orders</h1>
      <Container fluid>
        <Card>
          <CardBody>
            <Row>
              <div className="col-8">
                <input
                  type="text"
                  placeholder="Search by order ID, name, email, or status"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="form-control col-8"
                />
              </div>
              <div className="col-2">
                <select className="form-control" onChange={handleStatusFilter}>
                  <option value="" hidden disabled>
                    Select Status
                  </option>
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="dispatched">Dispatched</option>
                  <option value="delivered">Delivered</option>
                  <option value="failure">Failure</option>
                  <option value="pre-transit">Pre transit</option>
                  <option value="transit">Transit</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>
            </Row>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <div className="text-end t-flex t-flex-wrap t-gap-2 t-justify-end">
              <Btn
                attrBtn={{
                  className: 'mr-3',
                  color: 'primary',
                  disabled: !selectedRows?.length || !ingredientPrices || isDownloadingPdf,
                  onClick: downloadAllSelectedOrders,
                }}
              >
                {isDownloadingPdf ? 'Generating PDFs...' : 'Download'}
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-3',
                  color: 'primary',
                  disabled: !selectedRows?.length || !ingredientPrices || isDownloadingLabel,
                  onClick: downloadAllSelectedLabels,
                }}
              >
                {isDownloadingLabel ? 'Generating Labels...' : 'Labels'}
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-2',
                  color: 'secondary',
                  disabled: !selectedRows?.length || isDownloadingCsv,
                  onClick: downloadWeightCalculationsCSV,
                }}
              >
                {isDownloadingCsv ? 'Generating CSV...' : 'Download Weight CSV'}
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-2',
                  color: 'info',
                  disabled: !selectedRows?.length || isDownloadingSummary,
                  onClick: downloadSummaryOrdersCSV,
                }}
              >
                {isDownloadingSummary ? 'Generating Summary...' : 'Download Summary Orders'}
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-2',
                  color: 'info',
                  disabled: !selectedRows?.length,
                  onClick: createShipment,
                }}
              >
                Shipment
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-2',
                  color: 'info',
                  disabled: !selectedRows?.length,
                  onClick: getShipmentTracking,
                }}
              >
                Get Tracking
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-2',
                  color: 'info',
                  disabled: !selectedRows?.length || sendEmailUpdatesLoading,
                  onClick: sendEmailUpdatesSubmit,
                }}
              >
                Send Email Updates
              </Btn>
            </div>

            {isLoading && (
              <div className="text-center py-3 mt-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading orders...</p>
              </div>
            )}

            {!isLoading && (
              <div className="mb-3 text-muted mt-3">
                Showing {data.length} of {totalRows} orders
                {searchTerm && ` (filtered by "${searchTerm}")`}
                {statusFilter && ` (status: ${statusFilter})`}
              </div>
            )}

            <DataTable
              columns={tableColumns}
              data={data}
              striped={true}
              center={true}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              progressPending={isLoading}
              selectableRowsHighlight
              paginationPerPage={pageSize}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              paginationComponentOptions={{
                noRowsPerPage: false, // Hide the dropdown that allows changing the number of rows per page
              }}
              selectableRows
              onSelectedRowsChange={handleRowSelected}
              clearSelectedRows={false}
            />
          </CardBody>
        </Card>
        {externalOrder?.order_id ? (
          <>
            <Row>
              <Col sm="7">
                <Card>
                  <CardBody>
                    <div className="row">
                      <div className="col-sm-6">
                        <div className="text-start">
                          <h3>
                            Order # <span className="digits counter">{externalOrder?.order_id}</span>
                          </h3>
                          <p>
                            Preparation Date: {resolvePreparationDate(externalOrder).toDateString()}
                            <br />
                            Current Status:{' '}
                            <span style={{ textTransform: 'capitalize' }}>{externalOrder?.status}</span>
                          </p>
                        </div>
                      </div>
                      <div className="col-sm-6 text-end">
                        <div ref={shippingAddressRef} className="m-2 mt-0">
                          <h3>Shipping Address</h3>
                          <p>
                            {externalOrder?.user?.source_company_name}
                            <br />
                            {externalOrder?.user?.full_name}
                            <br />
                            {externalOrder?.user?.address}
                            <br />
                            {externalOrder?.user?.city}&nbsp;(
                            {externalOrder?.user?.zip_code})
                            <br />
                            {externalOrder?.user?.country}
                            <br />
                            {externalOrder?.user?.phone_number}
                          </p>
                        </div>
                        <Btn
                          attrBtn={{
                            className: 'mt-3',
                            color: 'primary',
                            onClick: () => printShipAddress(),
                          }}
                        >
                          <i class="fa fa-print me-1" /> {'Print'}
                        </Btn>
                      </div>
                    </div>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <h4> Total Material Cogs</h4>
                    <p>{totalOrderCogs.toFixed(2)}</p>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <Row>
                      <div className="col-sm-6">
                        <h4>Add Status</h4>
                        <select className="form-control" onChange={(e) => setStatus(e.target.value)}>
                          <option value="">--Select Status--</option>
                          <option value="Under Process">Under Process</option>
                          <option value="Ready to dispatch">Ready to dispatch</option>
                          <option value="Dispatched">Dispatched</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                        {showError.status === '' ? (
                          ''
                        ) : (
                          <span className="text-danger">Please select status</span>
                        )}
                        <input
                          type="text"
                          onChange={(e) => {
                            e.preventDefault();
                            setDetails(e.target.value);
                          }}
                          className="form-control mt-3"
                          placeholder="Tracking number"
                        />

                        <Btn
                          attrBtn={{
                            className: 'mt-3',
                            color: 'primary',
                            onClick: () => toggleModal(),
                          }}
                        >
                          {'Update Status'}
                        </Btn>
                      </div>
                    </Row>
                  </CardBody>
                </Card>
              </Col>

              <Col sm="5">
                <Card>
                  <CardBody className="personalized-formula">
                    <div className="table-responsive">
                      <div className="text-end">
                        <Btn
                          attrBtn={{
                            className: 'mr-2',
                            color: 'primary',
                            disabled: !externalOrder?.ingredients?.length,
                            onClick: () =>
                              downloadCsv(externalOrder?.ingredients, [
                                'ingredientId',
                                'ingredientName',
                                'quantity',
                              ]),
                          }}
                        >
                          Download
                        </Btn>
                      </div>
                      <table className="table accordion" id="accordionExample">
                        <thead>
                          <tr>
                            <th scope="col" className="col-8">
                              Ingredient Id
                            </th>
                            <th scope="col">Name</th>
                            <th scope="col">Quantity</th>
                            <th scope="col">Daily Cogs</th>
                          </tr>
                        </thead>
                        <tbody>
                          {externalOrder?.ingredients &&
                            externalOrder?.ingredients.map((item) => (
                              <tr key={'ingredient-' + item?.ingredientId}>
                                <td>{item.ingredientId}</td>
                                <td>{item.ingredientName}</td>
                                <td>{item.quantity}</td>
                                <td>{item?.cogs ? item.cogs : ''}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        ) : null}
        <ConfirmationModal
          message={'Are you sure to update the status?'}
          isOpen={modalOpen}
          toggle={toggleModal}
          onConfirm={handleConfirmAction}
        />
      </Container>
    </>
  );
};

export default ManualOrders;
